import type { UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { recordEvent } from "@/services/telemetry";

/**
 * Records each failed refresh of a polled read once. A failed poll keeps the last answer (Query holds previous data on
 * error), so a read that stopped updating looks like a quiet one; Honeycomb is where the failure shows.
 *
 * @param scope - The attribute namespace the event's context and error nest under, e.g. `currentRoutes`.
 */
export function useRefreshFailureEvent(
  query: UseQueryResult<unknown>,
  event: string,
  scope: string,
  context: Record<string, unknown>,
) {
  const lastRecordedErrorAt = useRef(query.errorUpdatedAt);
  useEffect(() => {
    if (query.errorUpdatedAt && query.errorUpdatedAt !== lastRecordedErrorAt.current && query.error) {
      lastRecordedErrorAt.current = query.errorUpdatedAt;
      recordEvent(event, {
        [scope]: {
          ...context,
          error: query.error instanceof Error ? query.error.message : String(query.error),
        },
      });
    }
  }, [query.errorUpdatedAt]);
}
